import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
  message,
  Modal,
  Switch,
  Alert,
  Divider,
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  SafetyOutlined, 
} from '@ant-design/icons';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useUpdateUserProfile } from '../hooks/useClientData';
import { supabase } from '@/shared/lib/supabase';
import { validateCpfCnpjUnique, validatePhoneUnique } from '../utils/validation';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Title, Paragraph, Text } = Typography;

interface ProfileFormValues {
  full_name: string;
  company: string;
  job_title?: string;
  phone?: string;
  cpf_cnpj?: string;
  postal_code?: string;
  address?: string;
  address_number?: string;
  complement?: string;
  city?: string;
  state?: string;
}

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// interface Session removed (session management hidden)

// Componente para máscara dinâmica de CPF/CNPJ
const CpfCnpjInput = ({ value, onChange, ...props }: any) => {
  const [localValue, setLocalValue] = useState(value || '');

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, ''); // Remove tudo que não é dígito
    let formatted = '';

    if (input.length <= 11) {
      // CPF: 000.000.000-00
      formatted = input
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      // CNPJ: 00.000.000/0000-00
      formatted = input
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }

    setLocalValue(formatted);
    if (onChange) {
      onChange(formatted);
    }
  };

  return (
    <Input
      {...props}
      value={localValue}
      onChange={handleChange}
      placeholder="000.000.000-00 ou 00.000.000/0000-00"
      maxLength={18}
    />
  );
};

// Componente para máscara de telefone
const PhoneInput = ({ value, onChange, ...props }: any) => {
  const [localValue, setLocalValue] = useState(value || '');

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, '');
    let formatted = '';

    if (input.length <= 10) {
      // (00) 0000-0000
      formatted = input
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
    } else {
      // (00) 00000-0000
      formatted = input
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
    }

    setLocalValue(formatted);
    if (onChange) {
      onChange(formatted);
    }
  };

  return (
    <Input
      {...props}
      value={localValue}
      onChange={handleChange}
      placeholder="(11) 99999-0000"
      maxLength={15}
    />
  );
};

// Componente para máscara de CEP
const CepInput = ({ value, onChange, onBlur, ...props }: any) => {
  const [localValue, setLocalValue] = useState(value || '');

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, '');
    const formatted = input.replace(/(\d{5})(\d{1,3})$/, '$1-$2');

    setLocalValue(formatted);
    if (onChange) {
      onChange(formatted);
    }
  };

  return (
    <Input
      {...props}
      value={localValue}
      onChange={handleChange}
      onBlur={onBlur}
      placeholder="00000-000"
      maxLength={9}
    />
  );
};

export default function ClientProfilePage() {
  const { user, loadUser } = useAuthStore();
  const profile = user?.profile;
  const userId = user?.id ?? null;

  const [profileForm] = Form.useForm<ProfileFormValues>();
  const [passwordForm] = Form.useForm<PasswordFormValues>();
  const [loadingCep, setLoadingCep] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Estados para 2FA
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loadingMfa, setLoadingMfa] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [mfaModalOpen, setMfaModalOpen] = useState(false);

  // Sessões ocultadas (recurso futuro)

  const updateProfile = useUpdateUserProfile();

  // Carregar dados do perfil
  useEffect(() => {
    if (profile) {
      profileForm.setFieldsValue({
        full_name: profile.full_name ?? '',
        company: profile.company_name ?? '',
        job_title: profile.job_title ?? '',
        phone: formatPhone(profile.phone ?? ''),
        cpf_cnpj: formatCpfCnpj(profile.cpf_cnpj ?? ''),
        postal_code: formatCep(profile.postal_code ?? ''),
        address: profile.address ?? '',
        address_number: profile.address_number ?? '',
        complement: profile.complement ?? '',
        city: profile.city ?? '',
        state: profile.state ?? '',
      });
    }
  }, [profile, profileForm]);

  const formatCpfCnpj = (value?: string | null) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const formatPhone = (value?: string | null) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 10) {
      return digits
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
    }
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
  };

  const formatCep = (value?: string | null) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{5})(\d{1,3})$/, '$1-$2');
  };

  // Verificar status do 2FA
  useEffect(() => {
    const checkMfaStatus = async () => {
      try {
        const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (error) throw error;
        
        // Se currentLevel é 'aal2', 2FA está ativo
        setMfaEnabled(data?.currentLevel === 'aal2');
      } catch (error) {
        console.error('Error checking MFA status:', error);
      }
    };

    if (userId) {
      checkMfaStatus();
    }
  }, [userId]);

  // Sessões não exibidas - implementação futura

  // Revogação de sessões removida (implementação futura)

  // Buscar endereço pelo CEP
  const handleCepBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const cep = e.target.value.replace(/\D/g, '');
    
    if (cep.length !== 8) {
      return;
    }

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (data.erro) {
        message.error('CEP não encontrado');
        return;
      }

      profileForm.setFieldsValue({
        address: data.logradouro || '',
        city: data.localidade || '',
        state: data.uf || '',
      });

      message.success('Endereço encontrado!');
    } catch (error) {
      message.error('Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  // Salvar perfil
  const handleProfileSubmit = async () => {
    if (!userId) return;
    try {
      const values = await profileForm.validateFields();

      if (profile?.cpf_cnpj && values.cpf_cnpj !== formatCpfCnpj(profile?.cpf_cnpj)) {
        message.error('CPF/CNPJ não pode ser alterado.');
        return;
      }

      if (profile?.phone && values.phone !== formatPhone(profile?.phone)) {
        message.error('Telefone não pode ser alterado.');
        return;
      }
      
      // Validar CPF/CNPJ único (se foi alterado)
      if (!profile?.cpf_cnpj && values.cpf_cnpj) {
        const cpfValidation = await validateCpfCnpjUnique(values.cpf_cnpj, userId);
        if (!cpfValidation.isValid) {
          message.error(cpfValidation.error);
          return;
        }
      }
      
      // Validar telefone único (se foi alterado)
      if (!profile?.phone && values.phone) {
        const phoneValidation = await validatePhoneUnique(values.phone, userId);
        if (!phoneValidation.isValid) {
          message.error(phoneValidation.error);
          return;
        }
      }
      
      const cleanValues = {
        full_name: values.full_name,
        company_name: values.company,
        job_title: values.job_title,
        phone: values.phone?.replace(/\D/g, ''),
        cpf_cnpj: values.cpf_cnpj?.replace(/\D/g, ''),
        postal_code: values.postal_code?.replace(/\D/g, ''),
        address: values.address,
        address_number: values.address_number,
        complement: values.complement,
        city: values.city,
        state: values.state,
      };
      
      await updateProfile.mutateAsync({
        userId,
        values: cleanValues,
      });
      await loadUser();
      message.success('Perfil atualizado com sucesso!');
    } catch (error: any) {
      // Capturar erros de constraint do banco
      if (error?.message?.includes('CPF/CNPJ já cadastrado')) {
        message.error('CPF/CNPJ já cadastrado em outra conta');
      } else if (error?.message?.includes('Telefone já cadastrado')) {
        message.error('Telefone já cadastrado em outra conta');
      } else {
        message.error(error?.message || 'Não foi possível salvar o perfil');
      }
    }
  };

  // Alterar senha
  const handlePasswordChange = async () => {
    try {
      setChangingPassword(true);
      const values = await passwordForm.validateFields();

      if (values.newPassword !== values.confirmPassword) {
        message.error('As senhas não coincidem');
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: values.newPassword,
      });

      if (error) throw error;

      message.success('Senha alterada com sucesso!');
      setPasswordModalOpen(false);
      passwordForm.resetFields();
    } catch (error: any) {
      console.error('Error changing password:', error);
      message.error(error?.message || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  // Habilitar 2FA
  const handleEnable2FA = async () => {
    try {
  setLoadingMfa(true);

      // Enroll MFA
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Autenticador FlowHub',
      });

      if (error) throw error;

      // Mostrar QR Code
      setQrCodeUrl(data.totp.qr_code);
      setMfaSecret(data.totp.secret);
      setMfaModalOpen(true);
    } catch (error: any) {
      console.error('Error enabling 2FA:', error);
      message.error(error?.message || 'Erro ao habilitar 2FA');
    } finally {
      setLoadingMfa(false);
    }
  };

  // Verificar código 2FA e finalizar ativação
  const handleVerify2FA = async () => {
    try {
      if (!verifyCode || verifyCode.length !== 6) {
        message.error('Digite um código válido de 6 dígitos');
        return;
      }

      const factors = await supabase.auth.mfa.listFactors();
      if (factors.error) throw factors.error;

      const totpFactor = factors.data?.totp?.[0];
      if (!totpFactor) {
        throw new Error('Fator TOTP não encontrado');
      }

      // Verificar o código
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: totpFactor.id,
        code: verifyCode,
      });

      if (error) throw error;

      message.success('2FA ativado com sucesso!');
      setMfaEnabled(true);
  setMfaModalOpen(false);
  setQrCodeUrl(null);
  setMfaSecret(null);
  setVerifyCode('');
    } catch (error: any) {
      console.error('Error verifying 2FA:', error);
      message.error(error?.message || 'Código inválido. Tente novamente.');
    }
  };

  // Desabilitar 2FA
  const handleDisable2FA = async () => {
    Modal.confirm({
      title: 'Desabilitar autenticação em dois fatores',
      content: 'Tem certeza que deseja desabilitar o 2FA? Sua conta ficará menos segura.',
      okText: 'Sim, desabilitar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setLoadingMfa(true);

          const factors = await supabase.auth.mfa.listFactors();
          if (factors.error) throw factors.error;

          const totpFactor = factors.data?.totp?.[0];
          if (!totpFactor) {
            message.warning('2FA não está ativo');
            return;
          }

          const { error } = await supabase.auth.mfa.unenroll({
            factorId: totpFactor.id,
          });

          if (error) throw error;

          message.success('2FA desabilitado com sucesso');
          setMfaEnabled(false);
        } catch (error: any) {
          console.error('Error disabling 2FA:', error);
          message.error(error?.message || 'Erro ao desabilitar 2FA');
        } finally {
          setLoadingMfa(false);
        }
      },
    });
  };

  // Sessões (ocultas) - colunas removidas

  return (
    <div className="space-y-6">
      <Row align="middle" justify="space-between">
        <Col>
          <Title level={3} style={{ marginBottom: 0, background: 'linear-gradient(135deg, #7c3aed, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Meu perfil
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Gerencie suas informações pessoais e configurações de segurança.
          </Paragraph>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Coluna Esquerda - Informações Pessoais */}
        <Col xs={24} lg={12}>
          <Card title="Informações pessoais" extra={<UserOutlined />}>
            <Alert
              type="info"
              showIcon
              description="Para alteração de email, CPF/CNPJ ou telefone, abra um chamado na Central de Suporte."
              style={{ marginBottom: 16 }}
            />
            <Form layout="vertical" form={profileForm} onFinish={handleProfileSubmit}>
              <Form.Item
                label="Nome completo"
                name="full_name"
                rules={[{ required: true, message: 'Informe seu nome completo' }]}
              >
                <Input placeholder="Ex: Ana Silva" />
              </Form.Item>

              <Form.Item label="Empresa" name="company">
                <Input placeholder="Nome da empresa" />
              </Form.Item>

              <Form.Item 
                label="CPF/CNPJ" 
                name="cpf_cnpj"
                rules={[
                  { required: true, message: 'CPF/CNPJ é obrigatório para contratar produtos' },
                ]}
                tooltip={profile?.cpf_cnpj ? "CPF/CNPJ não pode ser alterado após cadastro" : "Obrigatório para contratar produtos."}
              >
                <CpfCnpjInput disabled={!!profile?.cpf_cnpj} />
              </Form.Item>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Cargo" name="job_title">
                    <Input placeholder="Ex: Gerente de TI" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    label="Telefone" 
                    name="phone"
                    rules={[
                      { required: true, message: 'Telefone é obrigatório para pagamentos' },
                    ]}
                    tooltip="Obrigatório para pagamentos."
                  >
                    <PhoneInput disabled={!!profile?.phone} />
                  </Form.Item>
                </Col>
              </Row>

              <Card type="inner" title="Endereço" style={{ marginBottom: 16 }}>
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item 
                      label="CEP" 
                      name="postal_code"
                      rules={[{ required: true, message: 'CEP é obrigatório' }]}
                    >
                      <CepInput onBlur={handleCepBlur} />
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <Form.Item label="Rua/Avenida" name="address">
                      <Input placeholder="Será preenchido automaticamente" disabled={loadingCep} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item 
                      label="Número" 
                      name="address_number"
                      rules={[{ required: true, message: 'Número é obrigatório' }]}
                    >
                      <Input placeholder="123" />
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <Form.Item label="Complemento" name="complement">
                      <Input placeholder="Apto 45 (opcional)" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={16}>
                    <Form.Item label="Cidade" name="city">
                      <Input placeholder="Será preenchido automaticamente" disabled={loadingCep} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Estado" name="state">
                      <Input placeholder="UF" maxLength={2} disabled={loadingCep} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Form.Item label="E-mail cadastrado">
                <Input value={user?.email ?? ''} disabled />
              </Form.Item>

              <Space>
                <Button type="primary" htmlType="submit" loading={updateProfile.isPending}>
                  Salvar alterações
                </Button>
                <Button onClick={() => profileForm.resetFields()}>Cancelar</Button>
              </Space>
            </Form>
          </Card>
        </Col>

        {/* Coluna Direita - Segurança */}
        <Col xs={24} lg={12}>
          {/* Senha */}
          <Card title="Senha e Segurança" extra={<LockOutlined />} style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text strong>Alterar senha</Text>
                <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                  Recomendamos alterar sua senha regularmente para manter sua conta segura.
                </Paragraph>
                <Button 
                  icon={<LockOutlined />}
                  onClick={() => setPasswordModalOpen(true)}
                >
                  Alterar senha
                </Button>
              </div>

              <Divider />

              <div>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Text strong>Autenticação em dois fatores (2FA)</Text>
                      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Adicione uma camada extra de segurança à sua conta
                      </Paragraph>
                    </div>
                    <Switch 
                      checked={mfaEnabled}
                      loading={loadingMfa}
                      onChange={(checked) => {
                        if (checked) {
                          handleEnable2FA();
                        } else {
                          handleDisable2FA();
                        }
                      }}
                    />
                  </div>
                  {mfaEnabled && (
                    <Alert
                      message="2FA Ativo"
                      description="Sua conta está protegida com autenticação em dois fatores"
                      type="success"
                      showIcon
                      icon={<SafetyOutlined />}
                    />
                  )}
                </Space>
              </div>
            </Space>
          </Card>

          {/* Sessões Ativas ocultadas (recurso futuro) */}
        </Col>
      </Row>

      {/* Modal de Alteração de Senha */}
      <Modal
        title="Alterar senha"
        open={passwordModalOpen}
        onOk={handlePasswordChange}
        onCancel={() => {
          setPasswordModalOpen(false);
          passwordForm.resetFields();
        }}
        okText="Alterar senha"
        cancelText="Cancelar"
        confirmLoading={changingPassword}
      >
        <Form form={passwordForm} layout="vertical">
          <Alert
            message="Atenção"
            description="Após alterar sua senha, você permanecerá conectado neste dispositivo, mas será desconectado de todos os outros."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            label="Nova senha"
            name="newPassword"
            rules={[
              { required: true, message: 'Digite a nova senha' },
              { min: 8, message: 'A senha deve ter no mínimo 8 caracteres' },
            ]}
          >
            <Input.Password placeholder="Digite a nova senha" />
          </Form.Item>

          <Form.Item
            label="Confirmar nova senha"
            name="confirmPassword"
            rules={[
              { required: true, message: 'Confirme a nova senha' },
            ]}
          >
            <Input.Password placeholder="Digite novamente a nova senha" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Configuração 2FA */}
      <Modal
        title="Configurar Autenticação em Dois Fatores"
        open={mfaModalOpen}
        onOk={handleVerify2FA}
        onCancel={() => {
          setMfaModalOpen(false);
          setQrCodeUrl(null);
          setMfaSecret(null);
          setVerifyCode('');
        }}
        okText="Verificar e ativar"
        cancelText="Cancelar"
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="Configurar aplicativo autenticador"
            description="Escaneie o QR Code com seu aplicativo autenticador (Google Authenticator, Authy, etc.)"
            type="info"
            showIcon
          />

          {qrCodeUrl && (
            <div style={{ textAlign: 'center' }}>
              <img src={qrCodeUrl} alt="QR Code 2FA" style={{ maxWidth: 250 }} />
            </div>
          )}

          {mfaSecret && (
            <div>
              <Text strong>Ou digite este código manualmente:</Text>
              <Input value={mfaSecret} readOnly />
            </div>
          )}

          <Form.Item
            label="Digite o código de 6 dígitos do app"
            required
          >
            <Input
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              placeholder="000000"
              maxLength={6}
              size="large"
            />
          </Form.Item>
        </Space>
      </Modal>
    </div>
  );
}
